import { useMemo } from 'react'

import { getOfferBookedRooms, getOfferOrderId } from 'tripPlanner/utils/chatItinerary'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'

interface OfferBookingState {
  /** Per-room booked data from itinerary savedItemData, or undefined if unbooked/unhydrated. */
  savedBookedRooms: Array<DrawerHotelBookedRoom> | undefined
  /**
   * Order ID to pass to the offer-detail API so it enriches the response with
   * fresh bookedRooms. Undefined when savedBookedRooms is already populated —
   * we have the data locally and can skip the order fetch.
   */
  detailOrderId: string | undefined
  /**
   * Order ID for constructing booking detail links (e.g. header "Booking
   * details" button). Always populated when the offer is booked, regardless of
   * whether the API fetch is skipped via savedBookedRooms.
   */
  orderIdForLink: string | undefined
}

/**
 * Derives drawer-side booked state for an offer: prefers the itinerary's
 * persisted bookedRooms (from hydration), and only asks the offer-detail API
 * to enrich with an orderId when the itinerary hasn't hydrated that data yet.
 */
export function useOfferBookingState(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): OfferBookingState {
  const savedBookedRooms = useMemo(
    () => getOfferBookedRooms(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const orderIdForLink = useMemo(
    () => getOfferOrderId(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const detailOrderId = savedBookedRooms ? undefined : orderIdForLink

  return { savedBookedRooms, detailOrderId, orderIdForLink }
}
